<template>
  <div class="ac-reactance-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$XL=2πfL | XC=1/(2πfC)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>交流频率 f</label>
            <span class="val">{{ freq }} <small>Hz</small></span>
          </div>
          <div class="hud-item orange">
            <label>实时阻抗对比 (Ω)</label>
            <span class="val">XL:{{ xl.toFixed(0) }} | XC:{{ xc.toFixed(0) }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="bulb-glow"><feGaussianBlur stdDeviation="6" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 交流电源 -->
          <g transform="translate(40, 120)">
             <circle r="15" fill="none" stroke="#94a3b8" stroke-width="2" />
             <path d="M -8,0 Q -4,-8 0,0 T 8,0" fill="none" stroke="#fbbf24" stroke-width="2">
                <animate attributeName="d" :dur="`${Math.max(0.1, 2/freq)}s`" repeatCount="indefinite"
                         values="M -8,0 Q -4,-8 0,0 T 8,0; M -8,0 Q -4,8 0,0 T 8,0; M -8,0 Q -4,-8 0,0 T 8,0" />
             </path>
          </g>

          <!-- 电感支路 (Inductor Branch) -->
          <g transform="translate(100, 60)">
             <rect x="0" y="0" width="200" height="2" fill="#1e293b" />
             <!-- 感器模型 -->
             <path d="M 40,0 q 5,-15 10,0 q 5,-15 10,0 q 5,-15 10,0 q 5,-15 10,0" fill="none" stroke="#0ea5e9" stroke-width="3" />
             <text x="45" y="-20" fill="#0ea5e9" font-size="9" font-weight="900">电感 L (阻止高频)</text>
             <!-- 负载灯泡 (亮度反映电流) -->
             <circle cx="160" cy="0" r="10" :fill="lBulbColor" :filter="freq < 50 ? 'url(#bulb-glow)' : 'none'" />
             <text x="160" y="25" text-anchor="middle" fill="#64748b" font-size="8">L-支路亮度</text>
          </g>

          <!-- 电容支路 (Capacitor Branch) -->
          <g transform="translate(100, 180)">
             <rect x="0" y="0" width="200" height="2" fill="#1e293b" />
             <!-- 容器模型 -->
             <line x1="60" y1="-12" x2="60" y2="12" stroke="#f43f5e" stroke-width="4" />
             <line x1="70" y1="-12" x2="70" y2="12" stroke="#f43f5e" stroke-width="4" />
             <text x="45" y="30" fill="#f43f5e" font-size="9" font-weight="900">电容 C (阻止低频)</text>
             <!-- 负载灯泡 -->
             <circle cx="160" cy="0" r="10" :fill="cBulbColor" :filter="freq > 50 ? 'url(#bulb-glow)' : 'none'" />
             <text x="160" y="25" text-anchor="middle" fill="#64748b" font-size="8">C-支路亮度</text>
          </g>

          <!-- 连接导线 -->
          <path d="M 55,120 V 60 H 100 M 55,120 V 180 H 100" fill="none" stroke="#1e293b" stroke-width="2" />
        </svg>

        <div class="physics-caption">
           现象：随着频率 f 升高，电感越难“通过”，灯泡变暗；电容则越易“通过”，灯泡变亮。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">交流电源频率调节 (Frequency)：{{ freq }} Hz</label>
                 <el-slider v-model="freq" :min="1" :max="200" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">核心结论指示</label>
                 <div class="status-box">
                    <span :class="freq < 50 ? 'active' : ''">低频利于电感</span>
                    <span :class="freq > 150 ? 'active' : ''">高频利于电容</span>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-refresh"></i>
           <p><strong>实战物理：</strong>电感在交流电中产生“自感电动势”，频率越高、阻碍越大；而电容则是通过不断的充放电传递信号，频率越高、充放电越快、阻抗 $X_C$ 反而越小。<strong>观察灯泡：</strong>你可以像音响的分频器一样，利用这个原理让高音去电容、低音去电感，完美分离不同频率的信号！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ACReactanceViz',
  data() {
    return {
      freq: 50
    }
  },
  computed: {
    xl() { return 2 * Math.PI * this.freq * 0.1; }, // L = 100mH
    xc() { return 1 / (2 * Math.PI * this.freq * 400e-6); }, // C = 400uF
    lBulbColor() {
       // Brightness inverse to XL
       const alpha = Math.max(0.1, 1 - this.xl / 150);
       return `rgba(251, 191, 36, ${alpha})`;
    },
    cBulbColor() {
       // Brightness inverse to XC
       const alpha = Math.max(0.1, 1 - this.xc / 150);
       return `rgba(14, 165, 233, ${alpha})`;
    }
  }
}
</script>

<style scoped lang="scss">
.ac-reactance-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.status-box { display: flex; gap: 8px; span { padding: 2px 10px; background: #f1f5f9; color: #94a3b8; border-radius: 10px; font-size: 10px; font-weight: 800; &.active { background: #fee2e2; color: #ef4444; } } }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .flex-1 { flex: 1; } .items-center { align-items: center; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #fdf2f8; padding: 10px 14px; border-radius: 10px; border: 1px solid #fbcfe8; display: flex; align-items: center; gap: 10px; i { color: #db2777; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 11px; color: #be185d; line-height: 1.5; strong { color: #831843; } } }
</style>
